import axios from "axios";
import { reportingClient } from "./httpClients/reportingClient";

const SEARCH_STUDIES_PATH = "/api/PacsShare/searchStudies";
const SEARCH_SERIES_PATH = "/api/PacsShare/searchSeries";
const SHARE_LINK_PATH = "/api/PacsShare/shareLink";

/**
 * Client timeout for shareLink. Internal processing is ~90s; APIM allows 150s.
 * Do not auto-retry: a retry re-triggers a full PACS retrieve.
 */
export const PACS_SHARE_LINK_TIMEOUT_MS = 120000;

function envelope(data) {
  return data?.data ?? data ?? {};
}

function parseEnvelope(data) {
  const raw = envelope(data);
  const ok = raw.success ?? raw.Success;
  if (!ok) {
    const info = raw.errorInfo ?? raw.ErrorInfo ?? {};
    const msg = info.message ?? info.Message ?? "Request failed";
    const err = new Error(msg);
    err.code = info.code ?? info.Code;
    throw err;
  }
  return raw.payload ?? raw.Payload ?? null;
}

function pick(obj, ...keys) {
  for (const key of keys) {
    const value = obj?.[key];
    if (value != null && value !== "") return value;
  }
  return "";
}

function asArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload) return [];
  return [payload];
}

function normalizeStudy(raw) {
  return {
    studyInstanceUID: String(
      pick(raw, "studyInstanceUID", "StudyInstanceUID"),
    ),
    studyDate: String(pick(raw, "studyDate", "StudyDate")),
    studyTime: String(pick(raw, "studyTime", "StudyTime")),
    studyDescription: String(
      pick(raw, "studyDescription", "StudyDescription"),
    ),
    patientName: String(pick(raw, "patientName", "PatientName")),
    patientID: String(pick(raw, "patientID", "PatientID", "patientId", "PatientId")),
    patientBirthDate: String(
      pick(raw, "patientBirthDate", "PatientBirthDate"),
    ),
    patientSex: String(pick(raw, "patientSex", "PatientSex")),
    accessionNumber: String(
      pick(raw, "accessionNumber", "AccessionNumber"),
    ),
    modality: String(pick(raw, "modality", "Modality")),
    numberOfStudyRelatedSeries: pick(
      raw,
      "numberOfStudyRelatedSeries",
      "NumberOfStudyRelatedSeries",
    ),
    numberOfStudyRelatedInstances: pick(
      raw,
      "numberOfStudyRelatedInstances",
      "NumberOfStudyRelatedInstances",
    ),
  };
}

function normalizeSeries(raw) {
  return {
    seriesInstanceUID: String(
      pick(raw, "seriesInstanceUID", "SeriesInstanceUID"),
    ),
    studyInstanceUID: String(
      pick(raw, "studyInstanceUID", "StudyInstanceUID"),
    ),
    seriesNumber: String(pick(raw, "seriesNumber", "SeriesNumber")),
    seriesDescription: String(
      pick(raw, "seriesDescription", "SeriesDescription"),
    ),
    modality: String(pick(raw, "modality", "Modality")),
    numberOfSeriesRelatedInstances: pick(
      raw,
      "numberOfSeriesRelatedInstances",
      "NumberOfSeriesRelatedInstances",
    ),
  };
}

function normalizeShareLink(raw) {
  return {
    downloadUrl: String(pick(raw, "downloadUrl", "DownloadUrl")),
    expiresAtUtc: String(pick(raw, "expiresAtUtc", "ExpiresAtUtc")),
    studyInstanceUID: String(
      pick(raw, "studyInstanceUID", "StudyInstanceUID"),
    ),
  };
}

export function getPacsShareErrorMessage(error, fallback = "Request failed") {
  if (!error) return fallback;
  if (
    error.code === "ECONNABORTED" ||
    error.code === "TIMEOUT" ||
    /timeout/i.test(error.message || "")
  ) {
    return (
      error.message && !/^timeout of \d+ms exceeded$/i.test(error.message)
        ? error.message
        : "Timed out waiting for the PACS share link. The study may still be retrieving — do not retry immediately."
    );
  }
  const data = error.response?.data;
  if (data) {
    const raw = envelope(data);
    const info = raw.errorInfo ?? raw.ErrorInfo ?? {};
    const fromBody = info.message ?? info.Message ?? raw.message ?? raw.Message;
    if (fromBody) return String(fromBody);
  }
  if (error.message && String(error.message).trim()) {
    return String(error.message).trim();
  }
  return fallback;
}

async function postPacsShare(path, body, config) {
  try {
    const response = await reportingClient.post(path, body, config);
    return parseEnvelope(response);
  } catch (error) {
    if (axios.isAxiosError?.(error) || error?.response || error?.code) {
      const data = error.response?.data;
      if (data && typeof data === "object") {
        try {
          return parseEnvelope(data);
        } catch (parsed) {
          parsed.status = error.response?.status;
          throw parsed;
        }
      }
      if (error.code === "ECONNABORTED") {
        const timeoutErr = new Error(getPacsShareErrorMessage(error));
        timeoutErr.code = "TIMEOUT";
        throw timeoutErr;
      }
    }
    throw error;
  }
}

export async function searchPacsStudies({
  organisationId,
  accessionNumber,
  patientName = null,
  patientId = null,
  studyDateFrom = null,
  studyDateTo = null,
  modality = null,
}) {
  const payload = await postPacsShare(SEARCH_STUDIES_PATH, {
    organisationId,
    accessionNumber,
    patientName,
    patientId,
    studyDateFrom,
    studyDateTo,
    modality,
  });
  return asArray(payload).map(normalizeStudy);
}

export async function searchPacsSeries({ organisationId, studyInstanceUID }) {
  const payload = await postPacsShare(SEARCH_SERIES_PATH, {
    organisationId,
    studyInstanceUID,
  });
  return asArray(payload).map(normalizeSeries);
}

export async function createPacsShareLink({
  organisationId,
  accessionNumber,
  studyInstanceUID,
}) {
  const payload = await postPacsShare(
    SHARE_LINK_PATH,
    {
      organisationId,
      accessionNumber,
      studyInstanceUID,
    },
    { timeout: PACS_SHARE_LINK_TIMEOUT_MS },
  );
  return normalizeShareLink(payload);
}
